import {concentrationUnitTypePredicate, volumeUnitTypePredicate} from 'inventory/InventoryUnitUtil';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {
  PlateMapMaterialInputStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {
  RowMetadata,
  Table,
  TableColumnType,
  TableDimension,
  TableReorderLocks,
} from 'notebook/tables/TableConstants';
import {
  PlateMaterialInput,
  ReagentAssignmentGroup,
  TestOrderInfoById,
} from 'samples/plate-maps/PlateMapConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';

export interface BaseFillPlateTransferStructuredTableMetadata {
  plateMaterialInputs: Array<PlateMaterialInput>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  showMaterialInputColumn: boolean;
  testOrderInfoById: TestOrderInfoById;
}

export interface BaseFillPlateTransferStructuredTableRowMetadata extends RowMetadata {
  schemaId: string;
}

/**
 * Adapter describing & constraining the behavior of the transfer tables in the Fill Plate modal.
 *
 * These tables are never embedded in Notebook and never persisted. Their contents are determined by a
 * combination of preexisting values of Inventory items and user selections made in the Fill plate modal.
 */
export default abstract class BaseFillPlateTransferStructuredTableAdapter<
  TTableMetadata extends BaseFillPlateTransferStructuredTableMetadata,
  TRowMetadata extends BaseFillPlateTransferStructuredTableRowMetadata,
> extends GenericStructuredTableAdapter<TTableMetadata, TRowMetadata> {
  getDisplayName(): string {
    return 'Transfer';
  }

  injectFrontEndMetadata({column}: {column: StructuredTableColumn}): StructuredTableColumn {
    if (column.columnType === TableColumnType.UNIT_REFERENCE) {
      if (column.columnGroup === StructuredTableColumnGroups.FILL_PLATE_SOURCE_CONCENTRATION) {
        return {...column, frontEndMetadata: {unitTypesFilterPredicate: concentrationUnitTypePredicate}};
      } else if (column.columnGroup === StructuredTableColumnGroups.FILL_PLATE_TRANSFER_QUANTITY) {
        return {...column, frontEndMetadata: {unitTypesFilterPredicate: volumeUnitTypePredicate}};
      } else if (column.columnGroup === StructuredTableColumnGroups.FILL_PLATE_TARGET_CONCENTRATION) {
        return {...column, frontEndMetadata: {unitTypesFilterPredicate: concentrationUnitTypePredicate}};
      }
    }
    return column;
  }

  _getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
  }): Array<StructuredTableColumn> {
    const materialInputColumn: PlateMapMaterialInputStructuredTableColumn = {
      ...makeStructuredTableColumn({
        name: 'Material input',
        columnType: TableColumnType.PLATE_MAP_MATERIAL_INPUT,
      }),
      columnType: TableColumnType.PLATE_MAP_MATERIAL_INPUT, // need to re-specify to type narrow
      plateMaterialInputs: params.metadata.plateMaterialInputs,
      isReadOnly: params.metadata.plateMaterialInputs.length === 0,
    };
    return [
      makeStructuredTableColumn({
        name: '',
        columnType: TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON,
        isReadOnly: true,
        width: 32,
      }),
      ...(params.metadata.showMaterialInputColumn ? [materialInputColumn] : []),
      makeStructuredTableColumn({
        name: 'Source concentration',
        columnType: TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_SOURCE_CONCENTRATION,
      }),
      makeStructuredTableColumn({
        name: 'Units',
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_SOURCE_CONCENTRATION,
        width: 120,
      }),
      makeStructuredTableColumn({
        name: 'Transfer quantity',
        columnType: TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_TRANSFER_QUANTITY,
      }),
      makeStructuredTableColumn({
        name: 'Units',
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_TRANSFER_QUANTITY,
        width: 120,
      }),
      makeStructuredTableColumn({
        name: 'Final concentration',
        columnType: TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_TARGET_CONCENTRATION,
      }),
      makeStructuredTableColumn({
        name: 'Units',
        columnType: TableColumnType.UNIT_REFERENCE,
        columnGroup: StructuredTableColumnGroups.FILL_PLATE_TARGET_CONCENTRATION,
        width: 120,
      }),
    ];
  }

  toTable(transferTable: StructuredTable<TTableMetadata, TRowMetadata>): Table {
    const table = super.toTable(transferTable);
    return {
      ...table,
      allowAddRows: false,
      allowColumnEdits: false,
      allowNoRows: true,
      allowRowRemoval: false,
      lockedDims: true,
    };
  }

  allowReorderDimensions(): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}
